import { Card } from '@components/common/Card';
import { Modal } from '@components/common/Modal';
import { Icon } from '@components/Icon';
import { CLASS_IMAGE_PLACEHOLDER } from '@constants/classImage';
import type { Course } from '@app-types/courses';

export interface LandingCourseDetailModalProps {
  course: Course | null;
  isOpen: boolean;
  onClose: () => void;
}

const GRADE_LABEL: Record<Course['gradeLevel'], string> = {
  All: 'All grades',
  Preschool: 'Preschool',
  Elementary: 'Elementary',
  Middle: 'Middle School',
  'High School': 'High School',
  Adult: 'Adult',
};

export function LandingCourseDetailModal({
  course,
  isOpen,
  onClose,
}: LandingCourseDetailModalProps) {
  if (!course) {
    return null;
  }

  const gradeLabel = GRADE_LABEL[course.gradeLevel] ?? course.gradeLevel;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={course.name}
      maxWidth="560px"
      className="bhg-landing-course-detail-modal"
      closeButtonLabel="Close class details"
    >
      <div className="bhg-landing-course-detail">
        <div className="bhg-landing-course-detail__image-wrap">
          <img
            src={course.image || CLASS_IMAGE_PLACEHOLDER}
            alt=""
            className="bhg-landing-course-detail__image"
            loading="lazy"
            onError={(e) => {
              const el = e.currentTarget;
              if (el.src !== CLASS_IMAGE_PLACEHOLDER) {
                el.src = CLASS_IMAGE_PLACEHOLDER;
                el.onerror = null;
              }
            }}
          />
          <span className="bhg-landing-course-detail__badge">{gradeLabel}</span>
        </div>

        <dl className="bhg-landing-course-detail__meta">
          {course.time ? (
            <div className="bhg-landing-course-detail__meta-item">
              <dt>Time</dt>
              <dd>{course.time}</dd>
            </div>
          ) : null}
          {course.priceLabel ? (
            <div className="bhg-landing-course-detail__meta-item">
              <dt>Price</dt>
              <dd>{course.priceLabel}</dd>
            </div>
          ) : null}
          {course.ageRange?.trim() ? (
            <div className="bhg-landing-course-detail__meta-item">
              <dt>Ages</dt>
              <dd>{course.ageRange.trim()}</dd>
            </div>
          ) : null}
          {course.maxStudents != null ? (
            <div className="bhg-landing-course-detail__meta-item">
              <dt>Capacity</dt>
              <dd>Up to {course.maxStudents} students</dd>
            </div>
          ) : null}
        </dl>

        {course.allowsFamilyDiscount ? (
          <Card elevation={1} padding={false} className="bhg-landing-course-detail__discount">
            <Icon
              type="currency-dollar"
              style="filled"
              colorVariant="orange"
              backgroundShape="rounded-square"
              size="sm"
              aria-hidden
            />
            <p className="bhg-landing-course-detail__discount-text">
              {course.familyDiscountAmount != null && course.familyDiscountAmount > 0
                ? `Family discount available — save up to $${course.familyDiscountAmount.toFixed(2)} when more than one child from your family enrolls in this class.`
                : 'Family discount available when more than one child from your family enrolls in this class.'}
            </p>
          </Card>
        ) : null}

        {course.description &&
        course.fullDescription &&
        course.description !== course.fullDescription ? (
          <p className="bhg-landing-course-detail__excerpt">{course.description}</p>
        ) : null}

        {course.fullDescription ? (
          <div className="bhg-landing-course-detail__description">{course.fullDescription}</div>
        ) : null}
      </div>
    </Modal>
  );
}
